/**
 * Advanced chat settings — slide-out sheet opened from the Tools popover.
 * "Set once, rarely change" preferences. Every control writes straight to the
 * Supabase-backed chat preferences store (no Save button); session state
 * follows via the store subscriptions in useChatTools/ChatWorkspace.
 */
import { useState } from "react";
import { ChevronDown, RotateCcw, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AgentSelector } from "@/components/AgentSelector";
import { useChatPreferences } from "@/hooks/useChatPreferences";
import {
  DEFAULT_CHAT_PREFERENCES,
  type StreamTimeoutPreset,
  type UserChatPreferences,
} from "@shared/chatPreferences";
import type { OpenRouterModel } from "@/services/aiService";

const SECTIONS_UI_KEY = "deeply.advanced_settings_sections";

type SectionKey = "model" | "behavior" | "tools";

function readOpenSections(): Record<SectionKey, boolean> {
  const fallback = { model: true, behavior: true, tools: true };
  if (typeof window === "undefined") return fallback;
  try {
    const raw = localStorage.getItem(SECTIONS_UI_KEY);
    if (!raw) return fallback;
    return { ...fallback, ...(JSON.parse(raw) as Partial<Record<SectionKey, boolean>>) };
  } catch {
    return fallback;
  }
}

interface AdvancedSettingsPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availableModels: OpenRouterModel[];
  selectedAgentId: string | null;
  setSelectedAgentId: (value: string | null) => void;
  hasPersonalizationData: boolean;
  showPromptEnhancer: boolean;
  setShowPromptEnhancer: (open: boolean) => void;
  canEnhancePrompt: boolean;
}

function SettingRow({
  label,
  description,
  children,
}: {
  label: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-xs font-medium">{label}</span>
        {description && <span className="text-xs text-muted-foreground">{description}</span>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

function Section({
  title,
  open,
  onToggle,
  children,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <Collapsible open={open} onOpenChange={onToggle} className="border rounded-lg">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center justify-between p-3 text-sm font-medium hover:bg-muted/50 rounded-lg"
          aria-expanded={open}
        >
          {title}
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="px-3 pb-3 space-y-1">{children}</CollapsibleContent>
    </Collapsible>
  );
}

export function AdvancedSettingsPanel({
  open,
  onOpenChange,
  availableModels,
  selectedAgentId,
  setSelectedAgentId,
  hasPersonalizationData,
  showPromptEnhancer,
  setShowPromptEnhancer,
  canEnhancePrompt,
}: AdvancedSettingsPanelProps) {
  const { preferences, updatePreference, updatePreferences } = useChatPreferences();
  const [openSections, setOpenSections] = useState<Record<SectionKey, boolean>>(readOpenSections);

  const toggleSection = (key: SectionKey) => {
    setOpenSections((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try {
        localStorage.setItem(SECTIONS_UI_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const resetToDefaults = () => {
    // Keep identity-like selections (model/agent/prompt) — reset behavior settings only.
    const d = DEFAULT_CHAT_PREFERENCES;
    updatePreferences({
      thinkingMode: d.thinkingMode,
      webSearchMode: d.webSearchMode,
      contextMode: d.contextMode,
      chatMode: d.chatMode,
      useRag: d.useRag,
      ragLimit: d.ragLimit,
      ragSimilarityThreshold: d.ragSimilarityThreshold,
      ragHybridMode: d.ragHybridMode,
      ragHybridSearch: d.ragHybridSearch,
      ragUseRerank: d.ragUseRerank,
      autoEmbedDocuments: d.autoEmbedDocuments,
      autoEmbedThreads: d.autoEmbedThreads,
      streamTimeoutPreset: d.streamTimeoutPreset,
      streamCustomTimeoutMinutes: d.streamCustomTimeoutMinutes,
      autoSelectBest: d.autoSelectBest,
    } satisfies Partial<UserChatPreferences>);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col">
        <SheetHeader className="p-4 pb-2">
          <SheetTitle className="text-base">Advanced Settings</SheetTitle>
          <SheetDescription className="text-xs">
            Set once, rarely change. Changes save immediately and sync across your devices.
          </SheetDescription>
        </SheetHeader>
        <ScrollArea className="flex-1 px-4">
          <div className="space-y-3 pb-4">
            <Section title="Model & Performance" open={openSections.model} onToggle={() => toggleSection("model")}>
              <div className="space-y-1.5 py-1.5">
                <Label className="text-xs font-medium">Preferred model</Label>
                <Select
                  value={preferences.preferredModel ?? ""}
                  onValueChange={(v) => updatePreference("preferredModel", v)}
                >
                  <SelectTrigger className="h-8 text-xs" aria-label="Preferred model">
                    <SelectValue placeholder="Choose a model" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {availableModels.map((m) => (
                      <SelectItem key={m.id} value={m.id} className="text-xs">
                        {m.name || m.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <SettingRow label="Auto-select best model" description="Let the app pick per message">
                <Switch
                  checked={preferences.autoSelectBest}
                  onCheckedChange={(v) => updatePreference("autoSelectBest", v)}
                  aria-label="Auto-select best model"
                />
              </SettingRow>
              <div className="space-y-1.5 py-1.5">
                <Label className="text-xs font-medium">Stream timeout</Label>
                <Select
                  value={preferences.streamTimeoutPreset}
                  onValueChange={(v) => updatePreference("streamTimeoutPreset", v as StreamTimeoutPreset)}
                >
                  <SelectTrigger className="h-8 text-xs" aria-label="Stream timeout">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 minutes</SelectItem>
                    <SelectItem value="10">10 minutes</SelectItem>
                    <SelectItem value="15">15 minutes</SelectItem>
                    <SelectItem value="30">30 minutes</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                    <SelectItem value="none">No limit</SelectItem>
                  </SelectContent>
                </Select>
                {preferences.streamTimeoutPreset === "custom" && (
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min={1}
                      max={120}
                      value={preferences.streamCustomTimeoutMinutes}
                      onChange={(e) => {
                        const n = Number(e.target.value);
                        if (Number.isFinite(n)) updatePreference("streamCustomTimeoutMinutes", n);
                      }}
                      className="h-8 text-xs"
                      aria-label="Custom stream timeout in minutes"
                    />
                    <span className="text-xs text-muted-foreground shrink-0">minutes (1–120)</span>
                  </div>
                )}
              </div>
              <SettingRow label="Context mode" description="Default for new chats">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() =>
                    updatePreference("contextMode", preferences.contextMode === "MAX" ? "LITE" : "MAX")
                  }
                >
                  {preferences.contextMode}
                </Button>
              </SettingRow>
            </Section>

            <Section title="AI Behavior" open={openSections.behavior} onToggle={() => toggleSection("behavior")}>
              <SettingRow label="Thinking mode" description="Extended reasoning when supported">
                <Switch
                  checked={preferences.thinkingMode}
                  onCheckedChange={(v) => updatePreference("thinkingMode", v)}
                  aria-label="Thinking mode"
                />
              </SettingRow>
              <SettingRow label="Web search" description="Fetch live information">
                <Switch
                  checked={preferences.webSearchMode === "open"}
                  onCheckedChange={(v) => updatePreference("webSearchMode", v ? "open" : "closed")}
                  aria-label="Web search"
                />
              </SettingRow>
              <SettingRow label="Semantic search (RAG)" description="Search embedded documents by meaning">
                <Switch
                  checked={preferences.useRag}
                  onCheckedChange={(v) => updatePreference("useRag", v)}
                  aria-label="Semantic search (RAG)"
                />
              </SettingRow>
              <div className="space-y-1.5 py-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium">RAG results limit</Label>
                  <span className="text-xs text-muted-foreground tabular-nums">{preferences.ragLimit}</span>
                </div>
                <Slider
                  min={3}
                  max={20}
                  step={1}
                  value={[preferences.ragLimit]}
                  onValueChange={([v]) => updatePreference("ragLimit", v)}
                  aria-label="RAG results limit"
                />
              </div>
              <div className="space-y-1.5 py-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium">Similarity threshold</Label>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {preferences.ragSimilarityThreshold.toFixed(2)}
                  </span>
                </div>
                <Slider
                  min={0.2}
                  max={0.9}
                  step={0.05}
                  value={[preferences.ragSimilarityThreshold]}
                  onValueChange={([v]) => updatePreference("ragSimilarityThreshold", Math.round(v * 100) / 100)}
                  aria-label="RAG similarity threshold"
                />
              </div>
              <SettingRow label="Hybrid context" description="Combine explicit + semantic results">
                <Switch
                  checked={preferences.ragHybridMode}
                  onCheckedChange={(v) => updatePreference("ragHybridMode", v)}
                  aria-label="Hybrid context mode"
                />
              </SettingRow>
              <SettingRow label="Hybrid search" description="Keyword + vector retrieval">
                <Switch
                  checked={preferences.ragHybridSearch}
                  onCheckedChange={(v) => updatePreference("ragHybridSearch", v)}
                  aria-label="Hybrid search"
                />
              </SettingRow>
              <SettingRow label="Rerank results" description="Rerank top candidates (Cohere)">
                <Switch
                  checked={preferences.ragUseRerank}
                  onCheckedChange={(v) => updatePreference("ragUseRerank", v)}
                  aria-label="Rerank results"
                />
              </SettingRow>
              <SettingRow label="Auto-embed documents" description="Index new documents for RAG">
                <Switch
                  checked={preferences.autoEmbedDocuments}
                  onCheckedChange={(v) => updatePreference("autoEmbedDocuments", v)}
                  aria-label="Auto-embed documents"
                />
              </SettingRow>
              <SettingRow label="Auto-embed threads" description="Index chat threads for RAG">
                <Switch
                  checked={preferences.autoEmbedThreads}
                  onCheckedChange={(v) => updatePreference("autoEmbedThreads", v)}
                  aria-label="Auto-embed threads"
                />
              </SettingRow>
            </Section>

            <Section title="Tools" open={openSections.tools} onToggle={() => toggleSection("tools")}>
              <div className="space-y-1.5 py-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium">Prompt enhancer</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs gap-1"
                    disabled={!canEnhancePrompt}
                    onClick={() => {
                      setShowPromptEnhancer(!showPromptEnhancer);
                      onOpenChange(false);
                    }}
                    title={canEnhancePrompt ? "Build an enhanced prompt with AI" : "Type a prompt first"}
                  >
                    <Sparkles className="h-3 w-3" />
                    Build prompt
                  </Button>
                </div>
              </div>
              <div className="space-y-1.5 py-1.5">
                <Label className="text-xs font-medium">AI Agent</Label>
                <AgentSelector value={selectedAgentId} onValueChange={setSelectedAgentId} isActive={open} />
              </div>
              <SettingRow
                label="Personalization"
                description={
                  hasPersonalizationData
                    ? "Use your personality, worldview & goals"
                    : "Add profile data in Settings to enable"
                }
              >
                <Switch
                  checked={preferences.usePersonalization}
                  onCheckedChange={(v) => updatePreference("usePersonalization", v)}
                  disabled={!hasPersonalizationData}
                  aria-label="Personalization"
                />
              </SettingRow>
            </Section>

            <Button
              variant="ghost"
              size="sm"
              className="w-full h-8 text-xs text-muted-foreground gap-1.5"
              onClick={resetToDefaults}
            >
              <RotateCcw className="h-3 w-3" />
              Reset behavior settings to defaults
            </Button>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
